<script setup lang="ts">
import { GAME_TYPE } from "~/constants";
import useEventCommerce from "~/hooks/events/use-event-commerce";

export interface CardLiveProps {
  title?: string;
  geniusId?: string;
  sportType?: TSportType;
  gameType?: TGameType;
  commerceCoverage: TCommerceCategory[];
  team1Name?: string;
  team2Name?: string;
  team1Color?: string;
  team2Color?: string;
  team1LogoUrl?: string;
  team2LogoUrl?: string;
  isEventAvailableForUser?: boolean;
  statisticTooltipTitle?: string;
  sportTypeTooltipTitle?: string;
}

const props = defineProps<CardLiveProps>();
const { commerceType } = useEventCommerce(props.commerceCoverage);

const sportType = computed(() => (props.sportType || "other").toLowerCase());
const bannerImageUrl = computed(() => `/images/banners/live/${sportType.value}.png`);
const isPracticeGameType = computed(() => props.gameType === GAME_TYPE.TRAINING || Boolean(props.team1Name && !props.team2Name));
const isOtherGameType = computed(() => props.gameType === GAME_TYPE.OTHER || Boolean(!props.team1Name && !props.team2Name));
const bannerImageFallbackUrl = "/images/banners/live/other.png";
const hasStatistics = computed(() => Boolean(props.geniusId));
</script>

<template>
  <div v-motion-pop class="cardLive">
    <div class="relative">
      <div class="cardLive__bannerImageTitleWrap">
        <div class="cardLive__bannerImageTitle">
          <b style="background-color: var(--base-error-color); padding: 2px 8px; border-radius: 2px">Live</b>
          Watch now
        </div>
      </div>
      <BaseImage
        class="cardLive__bannerImage"
        :src="bannerImageUrl"
        :fallback-src="bannerImageFallbackUrl"
        alt="Live banner"
        width="full"
        :height="80"
      />
    </div>

    <div class="cardLive__content">
      <div class="flex flex-row justify-around">
        <div v-if="props.team1Name" class="relative w-1/2 flex flex-col items-center text-main">
          <BaseLogo
            :src="props.team1LogoUrl"
            :icon-background="props.team1Color"
            with-hover
            no-background
            :title="team1Name"
            with-initials
            fit="contain"
          />
          <h3 class="cardLive__teamTitle text-caption-medium">
            {{ props.team1Name }}
          </h3>
        </div>
        <div v-if="!(isPracticeGameType || isOtherGameType)" class="text-xs font-bold text-secondary pt-4">
          VS
        </div>
        <div v-if="props.team2Name" class="relative w-1/2 flex flex-col items-center text-main">
          <BaseLogo
            :src="props.team2LogoUrl"
            :icon-background="props.team2Color"
            with-hover
            no-background
            :title="team2Name"
            with-initials
            fit="contain"
          />
          <h3 class="cardLive__teamTitle text-caption-medium">
            {{ props.team2Name }}
          </h3>
        </div>
      </div>
      <div v-if="isOtherGameType" class="h-[60px] flex items-center justify-center">
        <p class="line-clamp-2 text-main text-base font-medium">
          {{ props.title }}
        </p>
      </div>
    </div>

    <div class="hr" role="separator" />

    <EventMetaInfo
      class="!mt-0 p-3"
      :title="props.title"
      :sport-type="props.sportType"
      :is-other-game-type="isOtherGameType"
      :is-practice-game-type="isPracticeGameType"
      :has-statistics="hasStatistics"
      :team-name="props.team1Name"
    />

    <div v-if="!props.isEventAvailableForUser" class="cardLive__lock text-caption-medium text-secondary">
      <IconLock class="z-10" />

      <span class="ml-2 z-10">{{ commerceType }}</span>
    </div>
  </div>
</template>

<style lang="scss">
.cardLive {
  @apply flex flex-col max-w-full
  overflow-hidden cursor-pointer
  transition duration-100
  hover:shadow-2xl;

  background-color: var(--base-card-bgColor);
  border-radius: var(--base-border-radius);

  .cardLive__content {
    @apply p-3 flex flex-col justify-between flex-1;

    .iconStatistic:hover path {
      fill: var(--base-error-color);
    }
  }

  .cardLive__bannerImageTitleWrap {
    @apply absolute top-0 left-0 w-full h-full flex justify-center items-center;
  }

  .cardLive__bannerImageTitle {
    @apply text-base font-black text-white uppercase italic;
  }

  .cardLive__bannerImage {
    @apply w-full object-cover rounded-t-sm h-20;
  }

  .cardLive__teamTitle {
    @apply h-4 max-w-full truncate mt-2;
  }

  .cardLive__lock {
    @apply px-2 py-2 overflow-hidden
    flex justify-center items-center;

    background-color: var(--base-card-bgSecondaryColor);
  }

  .hr {
    background: var(--base-background-color);
  }
}
</style>
